import React, { useState, useRef, useEffect } from "react";
import {
  AppBar,
  Box,
  Button,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Select,
  MenuItem,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { SelectChangeEvent } from "@mui/material/Select";

// Import icons for the sidebar items
import WhatshotIcon from "@mui/icons-material/Whatshot";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import AccessibilityNewIcon from "@mui/icons-material/AccessibilityNew";
import LocalDiningIcon from "@mui/icons-material/LocalDining";
import ScaleIcon from "@mui/icons-material/Scale";
import PieChartIcon from "@mui/icons-material/PieChart";
import FavoriteIcon from "@mui/icons-material/Favorite";
import OpacityIcon from "@mui/icons-material/Opacity";

// ------------------ Type Definitions ------------------
type AnimationType = "fade-up" | "zoom-in";

type CalculatorId =
  | "bmr"
  | "tdee"
  | "macro"
  | "bmi"
  | "repMax"
  | "calorie"
  | "ibw"
  | "bodyFat"
  | "workout"
  | "hydration";

interface CalculatorListItem {
  id: CalculatorId;
  name: string;
  component: React.ReactElement;
  ref: React.RefObject<HTMLDivElement | null>;
}

interface AnimatedProps {
  animation: AnimationType;
  delay?: number;
  children: React.ReactNode;
}

// ------------------ Animated Component ------------------
// Uses an intersection observer to animate items as they come into view.
function Animated({ animation, delay = 0, children }: AnimatedProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => {
            setVisible(true);
          }, delay);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [delay]);

  const baseTransition = "opacity 0.8s ease-out, transform 0.8s ease-out";
  let initialStyle: React.CSSProperties = {
    opacity: 0,
    transition: baseTransition,
  };
  if (animation === "fade-up") {
    initialStyle = {
      ...initialStyle,
      transform: "translateY(20px) scale(0.98)",
    };
  } else if (animation === "zoom-in") {
    initialStyle = { ...initialStyle, transform: "scale(0.95)" };
  }
  const visibleStyle: React.CSSProperties = {
    opacity: 1,
    transform: "translateY(0) scale(1)",
    transition: baseTransition,
  };

  return (
    <Box ref={ref} sx={visible ? visibleStyle : initialStyle}>
      {children}
    </Box>
  );
}

// ------------------ Calculator Components ------------------

// 1. BMR Calculator Component
function BmrCalculator({ isMetric }: { isMetric: boolean }) {
  const [weight, setWeight] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [gender, setGender] = useState<string>("male");
  const [bmr, setBmr] = useState<string | null>(null);

  const handleCalculate = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);
    if (!w || !h || !a) return;
    let result: number;
    if (isMetric) {
      result =
        gender === "male"
          ? 10 * w + 6.25 * h - 5 * a + 5
          : 10 * w + 6.25 * h - 5 * a - 161;
    } else {
      result =
        gender === "male"
          ? 66 + 6.23 * w + 12.7 * h - 6.8 * a
          : 655 + 4.35 * w + 4.7 * h - 4.7 * a;
    }
    setBmr(result.toFixed(2));
  };

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        backgroundColor: "#f2f2f2",
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
      }}
    >
      <Typography variant="h6" gutterBottom>
        BMR Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Find out how many calories you burn at rest.
      </Typography>
      <TextField
        label={`Weight (${isMetric ? "kg" : "lbs"})`}
        variant="outlined"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label={`Height (${isMetric ? "cm" : "inches"})`}
        variant="outlined"
        value={height}
        onChange={(e) => setHeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Age"
        variant="outlined"
        value={age}
        onChange={(e) => setAge(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Gender</InputLabel>
        <Select
          value={gender}
          label="Gender"
          onChange={(e: SelectChangeEvent) => setGender(e.target.value)}
        >
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
        </Select>
      </FormControl>
      <Button variant="contained" onClick={handleCalculate}>
        Calc BMR
      </Button>
      {bmr && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          Your BMR: {bmr} cal/day
        </Typography>
      )}
    </Paper>
  );
}

// 2. TDEE Calculator Component
function TdeeCalculator({ isMetric }: { isMetric: boolean }) {
  const [weight, setWeight] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [gender, setGender] = useState<string>("male");
  const [activity, setActivity] = useState<number>(1.2);
  const [tdee, setTdee] = useState<string | null>(null);

  const handleCalculate = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);
    if (!w || !h || !a) return;
    let bmr: number;
    if (isMetric) {
      bmr =
        gender === "male"
          ? 10 * w + 6.25 * h - 5 * a + 5
          : 10 * w + 6.25 * h - 5 * a - 161;
    } else {
      bmr =
        gender === "male"
          ? 66 + 6.23 * w + 12.7 * h - 6.8 * a
          : 655 + 4.35 * w + 4.7 * h - 4.7 * a;
    }
    const result = bmr * activity;
    setTdee(result.toFixed(2));
  };

  return (
    <Paper
      sx={{
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        TDEE Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        See how many calories you burn with activity.
      </Typography>
      <TextField
        label={`Weight (${isMetric ? "kg" : "lbs"})`}
        variant="outlined"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label={`Height (${isMetric ? "cm" : "inches"})`}
        variant="outlined"
        value={height}
        onChange={(e) => setHeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Age"
        variant="outlined"
        value={age}
        onChange={(e) => setAge(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Gender</InputLabel>
        <Select
          value={gender}
          label="Gender"
          onChange={(e: SelectChangeEvent) => setGender(e.target.value)}
        >
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Activity Level</InputLabel>
        <Select
          value={activity.toString()}
          label="Activity Level"
          onChange={(e: SelectChangeEvent) =>
            setActivity(parseFloat(e.target.value))
          }
        >
          <MenuItem value={1.2}>Sedentary</MenuItem>
          <MenuItem value={1.375}>Light Active</MenuItem>
          <MenuItem value={1.55}>Moderate</MenuItem>
          <MenuItem value={1.725}>Very Active</MenuItem>
          <MenuItem value={1.9}>Extra Active</MenuItem>
        </Select>
      </FormControl>
      <Button variant="contained" onClick={handleCalculate}>
        Calc TDEE
      </Button>
      {tdee && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          Your TDEE: {tdee} cal/day
        </Typography>
      )}
    </Paper>
  );
}

// 3. Macro Calculator Component
function MacroCalculator({ isMetric }: { isMetric: boolean }) {
  const [tdee, setTdee] = useState<string>("");
  const [macros, setMacros] = useState<{
    protein: string;
    carbs: string;
    fats: string;
  } | null>(null);

  const handleCalculate = () => {
    const tdeeVal = parseFloat(tdee);
    if (!tdeeVal) return;
    const protein = ((0.3 * tdeeVal) / 4).toFixed(2);
    const carbs = ((0.4 * tdeeVal) / 4).toFixed(2);
    const fats = ((0.3 * tdeeVal) / 9).toFixed(2);
    setMacros({ protein, carbs, fats });
  };

  return (
    <Paper
      sx={{
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Macro Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Get your daily protein, carbs, and fats.
      </Typography>
      <TextField
        label="TDEE (calories)"
        variant="outlined"
        value={tdee}
        onChange={(e) => setTdee(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Button variant="contained" onClick={handleCalculate}>
        Calc Macros
      </Button>
      {macros && (
        <Box sx={{ mt: 2 }}>
          <Typography>Protein: {macros.protein} g/day</Typography>
          <Typography>Carbs: {macros.carbs} g/day</Typography>
          <Typography>Fats: {macros.fats} g/day</Typography>
        </Box>
      )}
    </Paper>
  );
}

// 4. BMI Calculator Component
function BmiCalculator({ isMetric }: { isMetric: boolean }) {
  const [weight, setWeight] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [bmi, setBmi] = useState<string | null>(null);

  const handleCalculate = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    if (!w || !h) return;
    let result: number;
    if (isMetric) {
      const heightInMeters = h / 100;
      result = w / (heightInMeters * heightInMeters);
    } else {
      result = (w * 703) / (h * h);
    }
    setBmi(result.toFixed(2));
  };

  return (
    <Paper
      sx={{
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        BMI Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Check your weight with BMI.
      </Typography>
      <TextField
        label={`Weight (${isMetric ? "kg" : "lbs"})`}
        variant="outlined"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label={`Height (${isMetric ? "cm" : "inches"})`}
        variant="outlined"
        value={height}
        onChange={(e) => setHeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Button variant="contained" onClick={handleCalculate}>
        Calc BMI
      </Button>
      {bmi && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          Your BMI: {bmi}
        </Typography>
      )}
    </Paper>
  );
}

// 5. 1 Rep Max Calculator Component
function OneRepMaxCalculator({ isMetric }: { isMetric: boolean }) {
  const [weight, setWeight] = useState<string>("");
  const [reps, setReps] = useState<string>("");
  const [oneRepMax, setOneRepMax] = useState<string | null>(null);

  const handleCalculate = () => {
    const w = parseFloat(weight);
    const r = parseFloat(reps);
    if (!w || !r) return;
    const result = w * (1 + r / 30);
    setOneRepMax(result.toFixed(2));
  };

  return (
    <Paper
      sx={{
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        1 Rep Max Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Find your max lift with one rep.
      </Typography>
      <TextField
        label={`Weight (${isMetric ? "kg" : "lbs"})`}
        variant="outlined"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Reps"
        variant="outlined"
        value={reps}
        onChange={(e) => setReps(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Button variant="contained" onClick={handleCalculate}>
        Calc 1RM
      </Button>
      {oneRepMax && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          Estimated 1RM: {oneRepMax} {isMetric ? "kg" : "lbs"}
        </Typography>
      )}
    </Paper>
  );
}

// 6. Calorie Intake Calculator Component
function CalorieIntakeCalculator({ isMetric }: { isMetric: boolean }) {
  const [weight, setWeight] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [gender, setGender] = useState<string>("male");
  const [activity, setActivity] = useState<number>(1.2);
  const [goal, setGoal] = useState<string>("maintain");
  const [calories, setCalories] = useState<string | null>(null);

  const handleCalculate = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);
    if (!w || !h || !a) return;
    let bmr: number;
    if (isMetric) {
      bmr =
        gender === "male"
          ? 10 * w + 6.25 * h - 5 * a + 5
          : 10 * w + 6.25 * h - 5 * a - 161;
    } else {
      bmr =
        gender === "male"
          ? 66 + 6.23 * w + 12.7 * h - 6.8 * a
          : 655 + 4.35 * w + 4.7 * h - 4.7 * a;
    }
    let tdee = bmr * activity;
    if (goal === "lose") {
      tdee -= 500;
    } else if (goal === "gain") {
      tdee += 500;
    }
    setCalories(tdee.toFixed(2));
  };

  return (
    <Paper
      sx={{
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Calorie Intake Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        See how many calories you need each day.
      </Typography>
      <TextField
        label={`Weight (${isMetric ? "kg" : "lbs"})`}
        variant="outlined"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label={`Height (${isMetric ? "cm" : "inches"})`}
        variant="outlined"
        value={height}
        onChange={(e) => setHeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Age"
        variant="outlined"
        value={age}
        onChange={(e) => setAge(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Gender</InputLabel>
        <Select
          value={gender}
          label="Gender"
          onChange={(e: SelectChangeEvent) => setGender(e.target.value)}
        >
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Activity Level</InputLabel>
        <Select
          value={activity.toString()}
          label="Activity Level"
          onChange={(e: SelectChangeEvent) =>
            setActivity(parseFloat(e.target.value))
          }
        >
          <MenuItem value={1.2}>Sedentary</MenuItem>
          <MenuItem value={1.375}>Light Active</MenuItem>
          <MenuItem value={1.55}>Moderate</MenuItem>
          <MenuItem value={1.725}>Very Active</MenuItem>
          <MenuItem value={1.9}>Extra Active</MenuItem>
        </Select>
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Goal</InputLabel>
        <Select
          value={goal}
          label="Goal"
          onChange={(e: SelectChangeEvent) => setGoal(e.target.value)}
        >
          <MenuItem value="lose">Lose Weight</MenuItem>
          <MenuItem value="maintain">Maintain</MenuItem>
          <MenuItem value="gain">Gain Weight</MenuItem>
        </Select>
      </FormControl>
      <Button variant="contained" onClick={handleCalculate}>
        Calc Calories
      </Button>
      {calories && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          You need {calories} cal/day
        </Typography>
      )}
    </Paper>
  );
}

// 7. Ideal Body Weight Calculator Component
function IdealBodyWeightCalculator({ isMetric }: { isMetric: boolean }) {
  const [height, setHeight] = useState<string>("");
  const [gender, setGender] = useState<string>("male");
  const [idealWeight, setIdealWeight] = useState<string | null>(null);

  const handleCalculate = () => {
    const h = parseFloat(height);
    if (!h) return;
    let result: number;
    if (isMetric) {
      const inches = h / 2.54;
      const additional = Math.max(0, inches - 60) * 2.3;
      result = gender === "male" ? 50 + additional : 45.5 + additional;
    } else {
      const inches = h;
      const additional = Math.max(0, inches - 60) * 2.3;
      const idealKg = gender === "male" ? 50 + additional : 45.5 + additional;
      result = idealKg * 2.20462;
    }
    setIdealWeight(result.toFixed(2));
  };

  return (
    <Paper
      sx={{
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Ideal Body Weight Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        See what weight is best for you.
      </Typography>
      <TextField
        label={`Height (${isMetric ? "cm" : "inches"})`}
        variant="outlined"
        value={height}
        onChange={(e) => setHeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Gender</InputLabel>
        <Select
          value={gender}
          label="Gender"
          onChange={(e: SelectChangeEvent) => setGender(e.target.value)}
        >
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
        </Select>
      </FormControl>
      <Button variant="contained" onClick={handleCalculate}>
        Calc Ideal Weight
      </Button>
      {idealWeight && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          Ideal Weight: {idealWeight} {isMetric ? "kg" : "lbs"}
        </Typography>
      )}
    </Paper>
  );
}

// 8. Body Fat Calculator Component
function BodyFatCalculator({ isMetric }: { isMetric: boolean }) {
  const [weight, setWeight] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [age, setAge] = useState<string>("");
  const [gender, setGender] = useState<string>("male");
  const [bodyFat, setBodyFat] = useState<string | null>(null);

  const handleCalculate = () => {
    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);
    if (!w || !h || !a) return;
    let bmi: number;
    if (isMetric) {
      const heightInMeters = h / 100;
      bmi = w / (heightInMeters * heightInMeters);
    } else {
      bmi = (w * 703) / (h * h);
    }
    const result =
      gender === "male"
        ? 1.2 * bmi + 0.23 * a - 16.2
        : 1.2 * bmi + 0.23 * a - 5.4;
    setBodyFat(result.toFixed(2));
  };

  return (
    <Paper
      sx={{
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Body Fat Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Find your body fat percent.
      </Typography>
      <TextField
        label={`Weight (${isMetric ? "kg" : "lbs"})`}
        variant="outlined"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label={`Height (${isMetric ? "cm" : "inches"})`}
        variant="outlined"
        value={height}
        onChange={(e) => setHeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Age"
        variant="outlined"
        value={age}
        onChange={(e) => setAge(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Gender</InputLabel>
        <Select
          value={gender}
          label="Gender"
          onChange={(e: SelectChangeEvent) => setGender(e.target.value)}
        >
          <MenuItem value="male">Male</MenuItem>
          <MenuItem value="female">Female</MenuItem>
        </Select>
      </FormControl>
      <Button variant="contained" onClick={handleCalculate}>
        Calc Body Fat
      </Button>
      {bodyFat && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          Body Fat: {bodyFat}%
        </Typography>
      )}
    </Paper>
  );
}

// 9. Workout Intensity Calculator Component
function WorkoutIntensityCalculator({ isMetric }: { isMetric: boolean }) {
  const [age, setAge] = useState<string>("");
  const [restingHR, setRestingHR] = useState<string>("");
  const [intensity, setIntensity] = useState<number>(60);
  const [targetHR, setTargetHR] = useState<string | null>(null);

  const handleCalculate = () => {
    const a = parseFloat(age);
    const rHR = parseFloat(restingHR);
    if (!a || !rHR) return;
    const maxHR = 220 - a;
    const result = (maxHR - rHR) * (intensity / 100) + rHR;
    setTargetHR(result.toFixed(2));
  };

  return (
    <Paper
      sx={{
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Workout Intensity Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Get your best heart rate for exercise.
      </Typography>
      <TextField
        label="Age"
        variant="outlined"
        value={age}
        onChange={(e) => setAge(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Resting HR"
        variant="outlined"
        value={restingHR}
        onChange={(e) => setRestingHR(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Intensity (%)</InputLabel>
        <Select
          value={intensity.toString()}
          label="Intensity (%)"
          onChange={(e: SelectChangeEvent) =>
            setIntensity(parseInt(e.target.value))
          }
        >
          <MenuItem value={60}>60%</MenuItem>
          <MenuItem value={70}>70%</MenuItem>
          <MenuItem value={80}>80%</MenuItem>
        </Select>
      </FormControl>
      <Button variant="contained" onClick={handleCalculate}>
        Calc Target HR
      </Button>
      {targetHR && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          Target HR: {targetHR} bpm
        </Typography>
      )}
    </Paper>
  );
}

// 10. Hydration Calculator Component
function HydrationCalculator({ isMetric }: { isMetric: boolean }) {
  const [weight, setWeight] = useState<string>("");
  const [exercise, setExercise] = useState<string>("");
  const [hydration, setHydration] = useState<string | null>(null);

  const handleCalculate = () => {
    const w = parseFloat(weight);
    const ex = parseFloat(exercise) || 0;
    if (!w) return;
    let weightInKg = w;
    if (!isMetric) {
      weightInKg = w * 0.453592;
    }
    const waterMl = weightInKg * 35 + ex * 12;
    const waterLiters = waterMl / 1000;
    setHydration(waterLiters.toFixed(2));
  };

  return (
    <Paper
      sx={{
        backgroundImage:
          "radial-gradient(at bottom right, #548a77, transparent 80%)",
        p: 2,
        mb: 2,
      }}
    >
      <Typography variant="h6" gutterBottom>
        Hydration Calculator
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        See how much water you need to drink each day.
      </Typography>
      <TextField
        label={`Weight (${isMetric ? "kg" : "lbs"})`}
        variant="outlined"
        value={weight}
        onChange={(e) => setWeight(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <TextField
        label="Exercise (mins)"
        variant="outlined"
        value={exercise}
        onChange={(e) => setExercise(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      />
      <Button variant="contained" onClick={handleCalculate}>
        Calc Hydration
      </Button>
      {hydration && (
        <Typography variant="h6" sx={{ mt: 2 }}>
          Drink {hydration} liters/day
        </Typography>
      )}
    </Paper>
  );
}

// ------------------ Sidebar Mappings ------------------
const calculatorIcons: { [key in CalculatorId]: React.ReactElement } = {
  bmr: <WhatshotIcon />,
  tdee: <LocalFireDepartmentIcon />,
  macro: <FastfoodIcon />,
  bmi: <FitnessCenterIcon />,
  repMax: <AccessibilityNewIcon />,
  calorie: <LocalDiningIcon />,
  ibw: <ScaleIcon />,
  bodyFat: <PieChartIcon />,
  workout: <FavoriteIcon />,
  hydration: <OpacityIcon />,
};

const calculatorDescriptions: { [key in CalculatorId]: string } = {
  bmr: "Your BMR: calories you burn at rest.",
  tdee: "Your TDEE: total burn with activity.",
  macro: "Get your daily protein, carbs, and fats.",
  bmi: "Your BMI: a weight status number.",
  repMax: "Estimate your one rep max.",
  calorie: "Daily calorie need for your goal.",
  ibw: "Your ideal weight for good health.",
  bodyFat: "Your body fat percentage.",
  workout: "Best heart rate for your workout.",
  hydration: "How much water you need.",
};

// ------------------ Main Landing Component ------------------
export default function Resources() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isMetric, setIsMetric] = useState<boolean>(false);
  // Desktop: collapsible, hovering sidebar.
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(false);
  // Mobile: temporary drawer state.
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);

  // Define section refs for each calculator.
  const sectionRefs: {
    [key in CalculatorId]: React.RefObject<HTMLDivElement | null>;
  } = {
    bmr: useRef<HTMLDivElement | null>(null),
    tdee: useRef<HTMLDivElement | null>(null),
    macro: useRef<HTMLDivElement | null>(null),
    bmi: useRef<HTMLDivElement | null>(null),
    repMax: useRef<HTMLDivElement | null>(null),
    calorie: useRef<HTMLDivElement | null>(null),
    ibw: useRef<HTMLDivElement | null>(null),
    bodyFat: useRef<HTMLDivElement | null>(null),
    workout: useRef<HTMLDivElement | null>(null),
    hydration: useRef<HTMLDivElement | null>(null),
  };

  const calculatorsList: CalculatorListItem[] = [
    {
      id: "bmr",
      name: "BMR Calculator",
      component: <BmrCalculator isMetric={isMetric} />,
      ref: sectionRefs.bmr,
    },
    {
      id: "tdee",
      name: "TDEE Calculator",
      component: <TdeeCalculator isMetric={isMetric} />,
      ref: sectionRefs.tdee,
    },
    {
      id: "macro",
      name: "Macro Calculator",
      component: <MacroCalculator isMetric={isMetric} />,
      ref: sectionRefs.macro,
    },
    {
      id: "bmi",
      name: "BMI Calculator",
      component: <BmiCalculator isMetric={isMetric} />,
      ref: sectionRefs.bmi,
    },
    {
      id: "repMax",
      name: "1 Rep Max",
      component: <OneRepMaxCalculator isMetric={isMetric} />,
      ref: sectionRefs.repMax,
    },
    {
      id: "calorie",
      name: "Calorie Intake",
      component: <CalorieIntakeCalculator isMetric={isMetric} />,
      ref: sectionRefs.calorie,
    },
    {
      id: "ibw",
      name: "Ideal Weight",
      component: <IdealBodyWeightCalculator isMetric={isMetric} />,
      ref: sectionRefs.ibw,
    },
    {
      id: "bodyFat",
      name: "Body Fat",
      component: <BodyFatCalculator isMetric={isMetric} />,
      ref: sectionRefs.bodyFat,
    },
    {
      id: "workout",
      name: "Workout Intensity",
      component: <WorkoutIntensityCalculator isMetric={isMetric} />,
      ref: sectionRefs.workout,
    },
    {
      id: "hydration",
      name: "Hydration",
      component: <HydrationCalculator isMetric={isMetric} />,
      ref: sectionRefs.hydration,
    },
  ];

  // Scroll to the desired section.
  const scrollToSection = (id: CalculatorId) => {
    const ref = sectionRefs[id];
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Desktop sidebar: collapsible, hovering with a toggle button.
  const desktopSidebar = (
    <Box
      sx={{
        position: "fixed",
        top: 80,
        left: 0,
        width: sidebarCollapsed ? 60 : 250,
        transition: "width 0.3s ease",
        backgroundColor: "background.paper",
        boxShadow: 3,
        height: "calc(100vh - 80px)",
        overflow: "hidden",
        zIndex: 1000,
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {!sidebarCollapsed && <Typography variant="h6">Calculators</Typography>}
        <IconButton
          size="small"
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        >
          <MenuIcon />
        </IconButton>
      </Box>
      <List>
        {calculatorsList.map((calc) => (
          <ListItem key={calc.id} disablePadding>
            <Tooltip title={calc.name} placement="right" arrow>
              <ListItemButton onClick={() => scrollToSection(calc.id)}>
                <ListItemIcon>{calculatorIcons[calc.id]}</ListItemIcon>
                {!sidebarCollapsed && <ListItemText primary={calc.name} />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  // Mobile sidebar: temporary drawer.
  const mobileSidebar = (
    <Drawer
      variant="temporary"
      open={mobileOpen}
      onClose={() => setMobileOpen(false)}
      ModalProps={{ keepMounted: true }}
      sx={{
        "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
      }}
    >
      <Box sx={{ width: 250, p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Calculators
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={isMetric}
              onChange={() => setIsMetric(!isMetric)}
            />
          }
          label={isMetric ? "Metric" : "Imperial"}
          sx={{ mb: 2 }}
        />
        <List>
          {calculatorsList.map((calc) => (
            <ListItem key={calc.id} disablePadding>
              <ListItemButton
                onClick={() => {
                  scrollToSection(calc.id);
                  setMobileOpen(false);
                }}
              >
                <ListItemIcon>{calculatorIcons[calc.id]}</ListItemIcon>
                <ListItemText primary={calc.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>
    </Drawer>
  );

  return (
    <Box>
      <AppBar position="fixed" sx={{ backgroundColor: "#548a77" }}>
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen(true)}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Avro Fitness Calculators
          </Typography>
          {!isMobile && (
            <FormControlLabel
              control={
                <Switch
                  checked={isMetric}
                  onChange={() => setIsMetric(!isMetric)}
                />
              }
              label={isMetric ? "Metric" : "Imperial"}
            />
          )}
        </Toolbar>
      </AppBar>
      {isMobile ? mobileSidebar : desktopSidebar}
      {/* Vertical subtle gradient stripe: 50px wide, full screen height */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: "50%",
          transform: "translateX(-25px)",
          width: "50px",
          height: "100vh",
          zIndex: -1,
          pointerEvents: "none",
          background:
            "linear-gradient(to right, transparent, rgba(0,128,0,0.05) 50%, transparent)",
        }}
      />
      <Box
        component="main"
        sx={{
          ml: isMobile ? 0 : sidebarCollapsed ? 60 : 250,
          pt: 10,
          px: 2,
          maxWidth: 1280,
          margin: "auto",
        }}
      >
        <Animated animation="fade-up">
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h3">Welcome to Avro Fitness</Typography>
            <Typography variant="h6">
              Scroll down to see our tools in action.
            </Typography>
          </Box>
        </Animated>
        {calculatorsList.map((calc, index) => (
          <Animated key={calc.id} animation="fade-up" delay={200 * index}>
            <Box ref={calc.ref} sx={{ mb: 8 }}>
              {calc.component}
            </Box>
          </Animated>
        ))}
      </Box>
    </Box>
  );
}
